// The connection's own frames: authenticating, subscribing, keeping alive, and the answers to each
// (spec/09-realtime-protocol.md §1, §2, §3, §6.5; INFRA-18, #379).
//
// These are not in spec/09 §6's catalogue by name, because the catalogue lists what travels on a
// channel. They are what every principal says to the gateway itself:
//
//   auth.connect        `auth`, the first frame, within 5 s: the realtime token. Never the URL.
//   auth.refresh        `auth`, after `auth.expiring`: a new token for the same principal.
//   auth.expiring       `evt`, 60 s before the token expires. Carries nothing (§6.5).
//   channel.subscribe   `sub` with `ch`: a channel the principal may see (§3).
//   channel.unsubscribe `unsub` with `ch`.
//   realtime.ping       `ping`: the client's own keepalive. The gateway answers `ok` with
//                       `realtime.pong`, and neither is logged nor traced (spec/19 §7 rule 1 —
//                       both names are in `SILENT_OPERATIONS`).
//
// Every one of them is answered with `ok` or `err` whose `re` is the frame's `id` and whose `type`
// is the frame's `type`, so a client matches an answer to its question by id alone.
import { z } from "../zod.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { AUTH_CONNECT, AUTH_EXPIRING, AUTH_REFRESH, CHANNEL_SUBSCRIBE, CHANNEL_UNSUBSCRIBE, REALTIME_PING, REALTIME_PONG, } from "./constants.js";
import { realtimeMessage } from "./envelope.js";
export { AUTH_CONNECT, AUTH_EXPIRING, AUTH_REFRESH, CHANNEL_SUBSCRIBE, CHANNEL_UNSUBSCRIBE, REALTIME_PING, REALTIME_PONG, };
/**
 * The principals a realtime token names (spec/09 §1, §3). `display` is a staff-run station's countdown screen
 * (ADR-0033): its token comes from the screen's display link, and its only channel is `display:{stationId}`.
 * `boot_server` is a Clavian Boot server (ADR-0063, spec/25 §13.1): its token comes from the device token exchange as
 * device kind `boot_server`, and its only channel is `boot-server:{bootServerId}`.
 */
export const REALTIME_PRINCIPAL_KINDS = ['station', 'member', 'player', 'display', 'boot_server'];
export const RealtimePrincipalKindSchema = z.enum(REALTIME_PRINCIPAL_KINDS).openapi('RealtimePrincipalKind', {
    description: 'Who holds a realtime socket: a station, a member (staff), a player, a staff-run station’s countdown screen, or a ' +
        'Clavian Boot server.',
});
/** `p` of `auth.connect` and `auth.refresh`: the realtime token, and nothing else. */
export const RealtimeAuthPayloadSchema = z
    .object({
    /** A realtime token (spec/09 §1): a JWT, EdDSA, five minutes. */
    token: z.string().min(1).max(8192),
})
    .openapi('RealtimeAuthPayload', {
    description: 'The realtime token, in the first frame and in every refresh. Never in the URL (spec/09 §1).',
});
export const AuthConnectMessage = realtimeMessage('auth', AUTH_CONNECT, RealtimeAuthPayloadSchema);
export const AuthRefreshMessage = realtimeMessage('auth', AUTH_REFRESH, RealtimeAuthPayloadSchema);
/** `p` of the `ok` that answers `auth.connect` and `auth.refresh`. */
export const RealtimeAuthAcceptedPayloadSchema = z
    .object({
    kind: RealtimePrincipalKindSchema,
    /** `stn_…`, `mem_…`, `ply_…`, a display link's `sdl_…` or a boot server's `bsv_…`. */
    principalId: z.string().min(1),
    /** When this token stops working. `auth.expiring` arrives 60 s before it. */
    expiresAt: IsoDateTimeSchema,
})
    .openapi('RealtimeAuthAcceptedPayload', { description: 'The socket is authenticated, as this principal, until then.' });
/** `p` of `auth.expiring`: nothing — "fetch a new token and send `auth.refresh`" (spec/09 §6.5). */
export const AuthExpiringPayloadSchema = z
    .object({})
    .strict()
    .openapi('AuthExpiringPayload', { description: 'Nothing: fetch a new realtime token and send `auth.refresh`.' });
export const AuthExpiringMessage = realtimeMessage('evt', AUTH_EXPIRING, AuthExpiringPayloadSchema);
export const ChannelSubscribeMessage = realtimeMessage('sub', CHANNEL_SUBSCRIBE, z.looseObject({}).optional());
export const ChannelUnsubscribeMessage = realtimeMessage('unsub', CHANNEL_UNSUBSCRIBE, z.looseObject({}).optional());
/**
 * What an `err` says was wrong. Machine-readable and kebab-case, as a problem document's `code` is
 * (spec/08 §2.6).
 *
 *   forbidden        the principal may not see this channel (spec/09 §3), or may not send this
 *   invalid-frame    not JSON, not the envelope, or a payload that does not match its `type`
 *   not-subscribed   an `unsub` for a channel this socket does not hold
 *   unavailable      the gateway could not do it now (the api did not answer); try again
 */
export const REALTIME_ERROR_CODES = ['forbidden', 'invalid-frame', 'not-subscribed', 'unavailable'];
export const RealtimeErrorPayloadSchema = z
    .object({
    code: z.enum(REALTIME_ERROR_CODES),
    /** For a person reading a log. Never a secret, a token or another principal's data. */
    message: z.string().max(500),
})
    .openapi('RealtimeErrorPayload', { description: 'Why the gateway refused a frame (spec/09 §2, §3).' });
export const RealtimePingMessage = realtimeMessage('ping', REALTIME_PING, z.looseObject({}).optional());
//# sourceMappingURL=connection.js.map